import { FC, RefObject, useContext, useEffect, useMemo, useState } from "react";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { FlyoutLink } from "@app/shared/Flyouts";
import { scrubUserInput } from "@app/shared/utility/scrubUserInput";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { ServiceAreaEditorContext } from "@app/region-sets/editor/ServiceAreaEditorContext";
import { ServiceAreaEditorPane } from "@app/region-sets/editor/ServiceAreaEditorPane";
import { ZipFlyout } from "@app/region-sets/editor/ServiceAreaEditorZipFlyout";
import { EditorCounty, EditorZip, zipKey, countyKey } from "@app/region-sets/editor/types";

interface CountiesProps {
  countyPaneRef: RefObject<HTMLDivElement>;
}

const ServiceAreaEditorCounties: FC<CountiesProps> = (props: CountiesProps) => {
  const editorContext = useContext(ServiceAreaEditorContext);
  const [showFilter, setShowFilter] = useState(true);
  const [bulkStatusKey, setBulkStatusKey] = useState(0);

  useEffect(() => {
    editorContext.setCountyFilter("");
  }, [showFilter]);

  const filteredCounties = useMemo(() => {
    if (!editorContext.stateToCountyLookup) {
      return [];
    }
    const escapedCountyFilter = scrubUserInput(editorContext.countyFilter);
    // filter the current state's counties by the regex, case-insensitive
    const countyFilterRegex = new RegExp(`${escapedCountyFilter}`, "i");
    const counties = editorContext.stateToCountyLookup[editorContext.selectedState]
      ?.map((countySSA) => editorContext.countyLookup[countySSA])
      .filter((county) => !!editorContext.countyFilter ? county.name.match(countyFilterRegex) : true)
      || [];

    return counties;
  }, [editorContext.countyFilter, editorContext.countyLookup, editorContext.stateToCountyLookup, editorContext.selectedState]);

  const handleCountyCheckboxChange = (countySSA: string, statusCountyLookup?: { [countySSA: string]: EditorCounty }) => {
    const updatedCountyLookup = structuredClone(statusCountyLookup || editorContext.countyLookup);
    const wasCountyChecked = editorContext.countyLookup[countySSA].checkboxState === CheckboxStateEnum.Checked;

    updatedCountyLookup[countySSA].checkboxState = wasCountyChecked ? CheckboxStateEnum.Unchecked : CheckboxStateEnum.Checked;

    editorContext.setCountyLookup(updatedCountyLookup);
  }

  const getUpdatedStateCheckboxStatus = (stateAbbreviation: string, updatedCountyLookup: { [countySSA: string]: EditorCounty }, updatedZipLookup: { [zipKey: string]: EditorZip }) => {
    const stateCounties = editorContext.stateToCountyLookup[stateAbbreviation];
    const allCountiesIncluded = editorContext.stateToCountyLookup[editorContext.selectedState].every((countySSA) => {
      return updatedCountyLookup[countySSA].countyStatus !== RegionSetStatusType.Unknown &&
        (editorContext.countyToZipLookup[countySSA] || []).every((zipCode) => updatedZipLookup[zipKey(countySSA, zipCode)].checkboxState === CheckboxStateEnum.Checked);
    });
    const noCountiesIncluded = stateCounties.every((county) => updatedCountyLookup[county].countyStatus === RegionSetStatusType.Unknown);
    const newStatus = allCountiesIncluded ?
      CheckboxStateEnum.Checked :
      noCountiesIncluded ?
        CheckboxStateEnum.Unchecked :
        CheckboxStateEnum.Indeterminate;

    return newStatus;
  }

  const handleCountyStatusChange = (countySSA: string, newStatus: RegionSetStatusType) => {
    const updatedCountyLookup = structuredClone(editorContext.countyLookup);
    const updatedZipLookup = structuredClone(editorContext.zipLookup);
    const updatedStateLookup = structuredClone(editorContext.stateLookup);

    updatedCountyLookup[countySSA].countyStatus = newStatus;

    const zipsAffected = editorContext.countyToZipLookup[countySSA] || [];
    const stateAffected = editorContext.countyLookup[countySSA].state;

    const oldStatus = editorContext.countyLookup[countySSA].countyStatus;
    const countyIsBeingRemoved = newStatus === RegionSetStatusType.Unknown && oldStatus !== RegionSetStatusType.Unknown;
    const countyIsBeingIncluded = newStatus !== RegionSetStatusType.Unknown && oldStatus === RegionSetStatusType.Unknown;

    zipsAffected.forEach((zipCode) => {
      const currentZipCheckboxState = updatedZipLookup[zipKey(countySSA, zipCode)].checkboxState;
      updatedZipLookup[zipKey(countySSA, zipCode)].checkboxState = countyIsBeingRemoved ?
        CheckboxStateEnum.Unchecked :
        countyIsBeingIncluded ?
          CheckboxStateEnum.Checked :
          currentZipCheckboxState;
    });

    updatedStateLookup[stateAffected].checkboxState = getUpdatedStateCheckboxStatus(stateAffected, updatedCountyLookup, updatedZipLookup);

    editorContext.setCountyLookup(updatedCountyLookup);
    editorContext.setZipLookup(updatedZipLookup);
    editorContext.setStateLookup(updatedStateLookup);
  }

  const handleBulkCountyStatusChange = (newStatus: RegionSetStatusType) => {
    const updatedCountyLookup = structuredClone(editorContext.countyLookup);
    const updatedZipLookup = structuredClone(editorContext.zipLookup);
    const updatedStateLookup = structuredClone(editorContext.stateLookup);
    const selectedVisibleCountySSAs = filteredCounties.filter((county) => editorContext.countyLookup[county.countySSA].checkboxState !== CheckboxStateEnum.Unchecked).map((county) => county.countySSA);
    selectedVisibleCountySSAs.forEach((countySSA) => {
      updatedCountyLookup[countySSA].countyStatus = newStatus;
      if (newStatus === RegionSetStatusType.Unknown) {

        (editorContext.countyToZipLookup[countySSA] || []).forEach((zipCode) => {
          updatedZipLookup[zipKey(countySSA, zipCode)].checkboxState = CheckboxStateEnum.Unchecked;
        });
        // else if the OLD status was unselected and the new one isn't (that's implied in the else)
      } else if (editorContext.countyLookup[countySSA].countyStatus === RegionSetStatusType.Unknown) {
        (editorContext.countyToZipLookup[countySSA] || []).forEach((zipCode) => {
          updatedZipLookup[zipKey(countySSA, zipCode)].checkboxState = CheckboxStateEnum.Checked;
        });
      }
    });

    updatedStateLookup[editorContext.selectedState].checkboxState = getUpdatedStateCheckboxStatus(editorContext.selectedState, updatedCountyLookup, updatedZipLookup);

    editorContext.setCountyLookup(updatedCountyLookup);
    editorContext.setZipLookup(updatedZipLookup);
    editorContext.setStateLookup(updatedStateLookup);
    setBulkStatusKey(() => bulkStatusKey % 9 ? bulkStatusKey + 1 : 1);
  }

  const handleSelectAllCounties = () => {
    const updatedCountyLookup = structuredClone(editorContext.countyLookup);

    editorContext.stateToCountyLookup[editorContext.selectedState].forEach((countySSA) => {
      updatedCountyLookup[countySSA].checkboxState = CheckboxStateEnum.Checked;
    });

    editorContext.setCountyLookup(updatedCountyLookup);
  }

  const handleClearAllCounties = () => {
    const updatedCountyLookup = structuredClone(editorContext.countyLookup);

    editorContext.stateToCountyLookup[editorContext.selectedState].forEach((countySSA) => {
      updatedCountyLookup[countySSA].checkboxState = CheckboxStateEnum.Unchecked;
    });

    editorContext.setCountyLookup(updatedCountyLookup);
  }

  const bulkCountyStatusDropdownItems: DropdownItem<RegionSetStatusType>[] = useMemo(() => {
    const items = [
      {
        display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--active" /> Apply Active</>,
        key: RegionSetStatusType.Active,
        name: "Apply Active",
      },
    ];

    if (!editorContext.isZipProject) {
      items.push(
        {
          display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--approved" /> Apply Approved</>,
          key: RegionSetStatusType.Approved,
          name: "Apply Approved",
        },
        {
          display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--pending" /> Apply Pending</>,
          key: RegionSetStatusType.Pending,
          name: "Apply Pending",
        },
        {
          display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--opportunity" /> Apply Opportunity</>,
          key: RegionSetStatusType.Opportunity,
          name: "Apply Opportunity",
        },
      );
    }

    items.push(
      {
        display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--unselected" /> Unselect</>,
        key: RegionSetStatusType.Unknown,
        name: "Unselect",
      },
    );

    return items;
  }, [editorContext.isZipProject]);

  const countyStatusDropdownItems: DropdownItem<RegionSetStatusType>[] = [
    {
      display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--unselected" /> Unselected</>,
      key: RegionSetStatusType.Unknown,
      name: "Unselected",
    },
    {
      display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--active" /> Active</>,
      key: RegionSetStatusType.Active,
      name: "Active",
    },
  ];

  if (!editorContext.isZipProject) {
    countyStatusDropdownItems.push(
      {
        display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--approved" /> Approved</>,
        key: RegionSetStatusType.Approved,
        name: "Approved",
      },
      {
        display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--pending" /> Pending</>,
        key: RegionSetStatusType.Pending,
        name: "Pending",
      },
      {
        display: <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--opportunity" /> Opportunity</>,
        key: RegionSetStatusType.Opportunity,
        name: "Opportunity",
      },
    )
  }

  const countiesList = useMemo(() => {
    if (!editorContext.stateToCountyLookup) {
      return [];
    }

    const counties = filteredCounties;

    return counties.map((county) => {
      const countyHasZips = !!editorContext.countyToZipLookup[county[countyKey]] && editorContext.countyToZipLookup[county[countyKey]].length > 0;
      const countyZips = editorContext.countyToZipLookup[county[countyKey]] || [];
      const selectedCountyZipsCount = countyZips.filter((countyZip) => editorContext.zipLookup[zipKey(county.countySSA, countyZip)].checkboxState === CheckboxStateEnum.Checked).length;
      const isCountyPartial = selectedCountyZipsCount < countyZips.length && selectedCountyZipsCount > 0;

      const linkText = (
        <span id={county[countyKey]}>
          {county.countySSA} - {county.name} {isCountyPartial ? "(Partial County)" : ""}
        </span>
      );
      const selectedCountyStatusItem = countyStatusDropdownItems.find((item) => item.key === county.countyStatus);
      return (
        <div className="service-area-editor__county-item" key={county[countyKey]}>
          <span>
            <QESCheckbox
              containerClassName="d-inline"
              checked={county.checkboxState === CheckboxStateEnum.Checked}
              indeterminate={county.checkboxState === CheckboxStateEnum.Indeterminate}
              label=""
              aria-labelledby={county[countyKey]}
              onChange={() => handleCountyCheckboxChange(county[countyKey])}
              onLabelClick={(e) => e.preventDefault()}
              testId={`service-area-editor__state--${county[countyKey]}`}
            />
            {countyHasZips ? (
              <FlyoutLink
                classNames="qes-button-secondary p-0 d-inline"
                content={{
                  body: (
                    <ZipFlyout
                      selectedCountySSA={county[countyKey]}
                    />
                  ),
                  title: `${county[countyKey]} ${county.name} - Zip Level`,
                }}
                size="40"
                ignoreDomUpdatesInFlyout={true}
                testId={`service-area-editor__zip-code-flyout-link--${county[countyKey]}`}
              >
                {linkText}
              </FlyoutLink>
            ) : (
              linkText
            )}
          </span>
          <NewActionMenu
            alignRight={true}
            buttonClassNames="qes-button-secondary service-area-editor__county-status-selector"
            buttonText={selectedCountyStatusItem?.key || "Unselected"}
            buttonElement={<span>{selectedCountyStatusItem?.display || <><span className="service-area-editor__county-status-dot service-area-editor__county-status-dot--unselected" /> Unselected</>}</span>}
            dropupContainer={props.countyPaneRef}
            key={selectedCountyStatusItem.key}
            testId="service-area-editor-counties__county-status-dropdown"
          >
            {countyStatusDropdownItems.map((item) => {
              const isSelected = item.key === selectedCountyStatusItem.key;
              return (
                <NewActionMenuItem
                  display={item.display}
                  isSelected={isSelected}
                  key={item.key}
                  onClick={() => handleCountyStatusChange(county[countyKey], item.key)}
                  testId={`service-area-editor-counties__county-status-option--${item.key}`}
                />
              );
            })}
          </NewActionMenu>
        </div>
      )
    })
  }, [editorContext.selectedState, editorContext.stateToCountyLookup, editorContext.countyLookup, editorContext.countyFilter, editorContext.zipLookup]);

  const totalVisibleCountiesSelected = useMemo(() => {
    if (!editorContext.stateToCountyLookup || !editorContext.selectedState)
      return 0;

    return editorContext.stateToCountyLookup[editorContext.selectedState].filter((countySSA) => editorContext.countyLookup[countySSA].countyStatus !== RegionSetStatusType.Unknown).length;
  }, [editorContext.countyLookup, editorContext.selectedState]);

  const isBulkCountyStatusDisabled = useMemo(() => {
    if (!editorContext.stateToCountyLookup || !editorContext.selectedState)
      return true;

    return !editorContext.stateToCountyLookup[editorContext.selectedState].some((countySSA) => editorContext.countyLookup[countySSA].checkboxState !== CheckboxStateEnum.Unchecked);
  }, [editorContext.countyLookup, editorContext.selectedState]);

  const closeX = (
    <span
      className="d-inline-flex align-items-center qes-text-input__clear-button"
      onClick={() => {
        editorContext.setCountyFilter("");
      }}
    >
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path stroke="#32323c" strokeWidth="5" d="m0 0 100 100m0-100L0 100" /></svg>
    </span>
  );

  return (
    <ServiceAreaEditorPane
      title="Counties"
      bulkActionBar={
        <div className="service-area-editor__county-bulk-actions">
          <NewActionMenu
            buttonClassNames="qes-button-secondary"
            buttonText="Edit County Status"
            key={bulkStatusKey}
            // loading, disabled, same effect
            loading={isBulkCountyStatusDisabled}
            testId="service-area-editor-counties__bulk-status-dropdown"
          >
            {bulkCountyStatusDropdownItems.map((item) => {
              return (
                <NewActionMenuItem
                  display={item.display}
                  isSelected={false}
                  key={item.key}
                  onClick={() => handleBulkCountyStatusChange(item.key)}
                  testId={`service-area-editor-counties__bulk-status-option--${item.key}`}
                />
              )
            })}
          </NewActionMenu>
          <div>
            <QESButton
              qesType="secondary"
              qesTestId="service-area-editor__counties-select-all"
              onClick={handleSelectAllCounties}
              disabled={!!editorContext.countyFilter}
            >
              Select All
            </QESButton>
            <QESButton
              qesType="secondary"
              qesTestId="service-area-editor__counties-clear-all"
              onClick={handleClearAllCounties}
              disabled={!!editorContext.countyFilter}
            >
              Clear All
            </QESButton>
            <QESTextInput
              label="Filter Counties"
              hideLabel={true}
              testId="service-area-editor__counties-filter"
              className={`bg-white${showFilter ? "" : " d-none"}`}
              containerClassName="d-inline-flex my-1"
              value={editorContext.countyFilter}
              onChange={(e) => editorContext.setCountyFilter(e.target.value)}
              inputAppend={!!editorContext.countyFilter && closeX}
            />
            <QESButton
              qesType="secondary"
              qesTestId="service-area-editor__counties-hide-filter"
              onClick={() => setShowFilter((prevShowFilter) => !prevShowFilter)}
            >
              {showFilter ? "Hide Filter" : "Show Filter"}
            </QESButton>
          </div>
        </div>
      }
      body={countiesList}
      bodyRef={props.countyPaneRef}
      totalSelected={`${totalVisibleCountiesSelected} of ${(editorContext.stateToCountyLookup && editorContext.stateToCountyLookup[editorContext.selectedState]?.length) || 0}`}
    />
  );
}

export { ServiceAreaEditorCounties };